import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getList,
  listContacts,
  uploadCsv,
  bulkAddContacts,
  deleteContact,
} from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowLeft, Trash2, ClipboardPaste } from "lucide-react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

export default function ListDetail() {
  const { id } = useParams();
  const fileInput = useRef();
  const [list, setList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pasteText, setPasteText] = useState(
    "email,first_name,last_name,company\njohn@example.com,John,Doe,Acme"
  );
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    try {
      const [l, c] = await Promise.all([getList(id), listContacts(id)]);
      setList(l);
      setContacts(c);
    } catch (e) {
      toast.error("Failed to load list");
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadCsv(id, file);
      toast.success(`Added ${res.added} contacts. Skipped ${res.skipped}.`);
      refresh();
    } catch (err) {
      toast.error("CSV upload failed");
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const parsePasted = () => {
    // CSV parser that handles quoted fields and common delimiters
    const raw = pasteText.trim();
    if (!raw) return [];
    const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) return [];

    // Detect delimiter from first line
    const first = lines[0];
    const counts = {
      ",": (first.match(/,/g) || []).length,
      "\t": (first.match(/\t/g) || []).length,
      ";": (first.match(/;/g) || []).length,
    };
    const delim = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

    // Quote-aware line splitter
    const splitLine = (line) => {
      const out = [];
      let cur = "";
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQ && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQ = !inQ;
          }
        } else if (ch === delim && !inQ) {
          out.push(cur.trim());
          cur = "";
        } else {
          cur += ch;
        }
      }
      out.push(cur.trim());
      return out;
    };

    const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
    const hasHeader = headers.some((h) => /email|mail|name|company|first|last/.test(h));

    // If first line looks like data (contains an email), treat every line as data
    if (!hasHeader) {
      const rows = [];
      for (const line of lines) {
        const cols = splitLine(line);
        // find email in any column
        const emailCol = cols.find((v) => /@/.test(v));
        if (emailCol) rows.push({ email: emailCol });
      }
      return rows;
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = splitLine(lines[i]);
      const row = {};
      headers.forEach((h, j) => (row[h] = cols[j] || ""));
      rows.push(row);
    }
    return rows;
  };

  const handleDelete = async (contactId) => {
    try {
      await deleteContact(contactId);
      refresh();
    } catch (e) {
      toast.error("Failed to delete contact");
    }
  };

  const handlePaste = async () => {
    const rows = parsePasted();
    if (rows.length === 0) {
      toast.error("No rows detected. Make sure your text has an email column.");
      return;
    }
    try {
      const res = await bulkAddContacts(id, rows);
      if (res.added === 0 && res.total_rows > 0) {
        toast.error(
          `No contacts added. ${res.skipped_invalid} rows had no valid email. Check header name (email, first_name, last_name, company).`
        );
      } else if (res.added === 0) {
        toast.error("Nothing to import.");
      } else {
        const dupNote = res.skipped_duplicate ? ` (${res.skipped_duplicate} duplicate)` : "";
        const invNote = res.skipped_invalid ? ` (${res.skipped_invalid} invalid)` : "";
        toast.success(`Added ${res.added} contacts.${dupNote}${invNote}`);
      }
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to add contacts";
      toast.error(msg);
    }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="list-detail-page">
      <Link
        to="/lists"
        className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] mb-4"
        data-testid="link-back-lists"
      >
        <ArrowLeft size={14} /> Back to lists
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">Audience list</div>
          <h1
            className="text-3xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
            data-testid="list-name"
          >
            {list?.name || "Loading..."}
          </h1>
          <p className="text-sm text-[#475569] mt-2">
            {contacts.length} contacts • {list?.description || "No description"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Upload panel */}
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="section-label mb-4">Add contacts</div>
          <Tabs defaultValue="csv">
            <TabsList className="grid grid-cols-2 mb-4 bg-[#F1F5F9]">
              <TabsTrigger value="csv" data-testid="tab-csv">
                <Upload size={13} className="mr-1.5" /> CSV Upload
              </TabsTrigger>
              <TabsTrigger value="paste" data-testid="tab-paste">
                <ClipboardPaste size={13} className="mr-1.5" /> Paste
              </TabsTrigger>
            </TabsList>

            <TabsContent value="csv">
              <label
                htmlFor="csv-upload"
                className="block border-2 border-dashed border-[#CBD5E1] hover:border-[#002FA7] rounded-lg p-8 text-center cursor-pointer transition-colors bg-[#F8FAFC]"
                data-testid="csv-dropzone"
              >
                <Upload size={22} className="mx-auto text-[#94A3B8] mb-2" strokeWidth={1.8} />
                <div className="text-sm font-medium text-[#0F172A]">
                  {uploading ? "Uploading…" : "Click to upload CSV"}
                </div>
                <div className="text-xs text-[#94A3B8] mt-1">
                  Columns: email, first_name, last_name, company, +custom
                </div>
                <input
                  ref={fileInput}
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="input-csv-file"
                />
              </label>
            </TabsContent>

            <TabsContent value="paste">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                className="field-input font-mono text-xs h-40 resize-none"
                data-testid="textarea-paste"
              />
              <div className="text-xs text-[#94A3B8] mt-2">
                First row = headers (email required). Comma or tab-separated.
              </div>
              <button
                onClick={handlePaste}
                className="btn-primary w-full mt-4"
                data-testid="btn-import-paste"
              >
                Import Contacts
              </button>
            </TabsContent>
          </Tabs>
        </div>

        {/* Contacts table */}
        <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <div className="section-label">Contacts</div>
            <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
              {contacts.length} total
            </div>
          </div>
          {contacts.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#94A3B8]" data-testid="empty-contacts">
              Upload a CSV or paste contacts to get started.
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#F8FAFC]">
                  <tr className="border-b border-[#E2E8F0]">
                    <Th>Email</Th>
                    <Th>Name</Th>
                    <Th>Company</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-slate-50">
                      <td className="px-6 py-3 text-sm text-[#0F172A] font-medium" data-testid={`contact-email-${c.id}`}>
                        {c.email}
                      </td>
                      <td className="px-6 py-3 text-sm text-[#475569]">
                        {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="px-6 py-3 text-sm text-[#475569]">{c.company || "—"}</td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-[#94A3B8] hover:text-[#E11D48] p-1"
                          data-testid={`btn-delete-contact-${c.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748B] text-left">
    {children}
  </th>
);
